/**
 * Carries an MCP consent URL across sign-in. Core sends the browser to
 * `/connect/consent/<id>`; a signed-out visitor is bounced to the login page
 * with `?redirect=`, but every sign-in flow (password, MFA, OTP, social) ends
 * on `paths.home`, so the landing redirect reads it back from here.
 */
const STORAGE_KEY = 'mcp-consent-return'
const TTL_MS = 10 * 60 * 1000
const CONSENT_PATH_RE = /^\/connect\/consent\/[A-Za-z0-9_-]{1,128}$/

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const browserStorage = (): KeyValueStorage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

export const isConsentReturnPath = (
  value: string | null | undefined
): value is string => !!value && CONSENT_PATH_RE.test(value)

export const rememberConsentReturnPath = (
  path: string | null | undefined,
  storage: KeyValueStorage | null = browserStorage(),
  now: number = Date.now()
) => {
  if (!storage || !isConsentReturnPath(path)) return
  storage.setItem(STORAGE_KEY, JSON.stringify({ path, at: now }))
}

export const peekConsentReturnPath = (
  storage: KeyValueStorage | null = browserStorage(),
  now: number = Date.now()
): string | null => {
  const raw = storage?.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown }
    const fresh = typeof at === 'number' && now - at <= TTL_MS

    return fresh && typeof path === 'string' && isConsentReturnPath(path)
      ? path
      : null
  } catch {
    return null
  }
}

export const clearConsentReturnPath = (
  storage: KeyValueStorage | null = browserStorage()
) => {
  storage?.removeItem(STORAGE_KEY)
}
